import { useEffect, useState } from "react"
import { Link, useNavigate, useParams } from "react-router"
import { client } from "@client/client"
import { getAcademicAssessment } from "@shared/academic"
import { DonorChildDetailSkeleton } from "./components/skeleton"
import type { SponsorAcademicTerm, SponsorChild } from "./sponsor-session-context"

interface ChildDetail {
  child: SponsorChild
  academicTerms: SponsorAcademicTerm[]
}

function formatSize(bytes: number) {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`
  return `${bytes} B`
}

function documentLabel(purpose: string) {
  if (purpose === "child-report") return "Report card"
  if (purpose === "child-document") return "Document"
  return purpose.replace(/-/g, " ").replace(/\b\w/g, (character) => character.toUpperCase())
}

function TermCard({
  term,
  school,
  classLevel,
  expanded,
  onToggle,
}: {
  term: SponsorAcademicTerm
  school: string
  classLevel: string
  expanded: boolean
  onToggle: () => void
}) {
  const scoredResults = term.results.filter((result) => result.score !== null)
  const average = scoredResults.length > 0
    ? Math.round(scoredResults.reduce((total, result) => total + (result.score ?? 0), 0) / scoredResults.length)
    : null
  const assessment = getAcademicAssessment(average)

  return (
    <div className="overflow-hidden rounded-[10px] border border-gold-500 bg-white">
      <button
        type="button"
        aria-expanded={expanded}
        onClick={onToggle}
        className="flex w-full items-center justify-between gap-4 p-5 text-left transition-colors hover:bg-cream-50"
      >
        <div className="min-w-0">
          <h3 className="m-0 font-display text-lg font-bold text-forest-700">{term.session} · {term.term}</h3>
          <p className="m-0 mt-1 truncate text-sm text-ink/60">{school} · {classLevel}</p>
        </div>
        <div className="flex shrink-0 items-center gap-4">
          <div className="hidden text-right sm:block">
            <p className="m-0 text-sm font-medium text-ink">
              {average === null ? "No scores" : `${average} (${assessment.grade})`}
            </p>
            <p className="m-0 text-xs text-ink/50">
              {term.results.length} subject{term.results.length === 1 ? "" : "s"}
            </p>
          </div>
          <svg
            className={`h-5 w-5 text-ink/50 transition-transform ${expanded ? "rotate-180" : ""}`}
            viewBox="0 0 20 20"
            fill="none"
            aria-hidden="true"
          >
            <path d="m5 7.5 5 5 5-5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
      </button>

      <div
        className={`grid transition-[grid-template-rows,opacity] duration-300 ease-out ${expanded ? "grid-rows-[1fr] opacity-100" : "pointer-events-none grid-rows-[0fr] opacity-0"}`}
        aria-hidden={!expanded}
      >
        <div className="min-h-0 overflow-hidden">
          <div className="border-t border-gold-500/40 p-5">
            <div className="grid gap-3 sm:grid-cols-3">
              <div className="rounded-lg bg-cream-100 p-3">
                <p className="m-0 text-xs font-medium text-ink/60">Average score</p>
                <p className="m-0 mt-1 text-lg font-semibold text-ink">{average === null ? "—" : `${average}/100`}</p>
              </div>
              <div className="rounded-lg bg-cream-100 p-3">
                <p className="m-0 text-xs font-medium text-ink/60">Attendance</p>
                <p className="m-0 mt-1 text-lg font-semibold text-ink">{term.attendance === null ? "—" : `${term.attendance}%`}</p>
              </div>
              <div className="rounded-lg bg-cream-100 p-3">
                <p className="m-0 text-xs font-medium text-ink/60">Position</p>
                <p className="m-0 mt-1 text-lg font-semibold text-ink">{term.position || "—"}</p>
              </div>
            </div>

            <div className="mt-5 flex flex-col gap-3">
              {term.results.length === 0 ? (
                <p className="m-0 rounded-lg bg-cream-100 p-4 text-sm text-ink/60">No subjects recorded for this term.</p>
              ) : term.results.map((result) => (
                <div key={result.id} className="rounded-lg border border-gold-500/40 p-4">
                  <h4 className="m-0 text-sm font-semibold text-ink">{result.subject.name}</h4>
                  <p className="m-0 mt-1 text-sm text-ink/60">
                    {result.score === null
                      ? "Not yet scored"
                      : `${result.score}/100 · ${result.grade ?? "—"} · ${result.proficiency ?? "—"}`}
                  </p>
                </div>
              ))}
            </div>

            {term.teacherSummary && (
              <div className="mt-5 rounded-lg bg-cream-100 p-4">
                <p className="m-0 text-xs font-medium uppercase tracking-wide text-ink/50">Teacher summary</p>
                <p className="m-0 mt-2 whitespace-pre-wrap text-sm text-ink/70">{term.teacherSummary}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export function DonorChildDetailPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [detail, setDetail] = useState<ChildDetail | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [selectedTermId, setSelectedTermId] = useState("")

  useEffect(() => {
    if (!id) return
    let cancelled = false
    const load = async () => {
      const response = await client.api.sponsor.portal.children[":id"].$get({ param: { id } })
      if (response.status === 401) {
        navigate("/donor-signin", { replace: true })
        return
      }
      if (!response.ok) {
        if (!cancelled) setError("We couldn't load this child's profile.")
        return
      }
      const body = await response.json()
      if (!cancelled && "data" in body) {
        setDetail(body.data)
        setSelectedTermId(body.data.academicTerms[0]?.id ?? "")
      }
    }
    void load().catch(() => {
      if (!cancelled) setError("We couldn't load this child's profile.")
    })
    return () => {
      cancelled = true
    }
  }, [id, navigate])

  if (error) {
    return (
      <div className="flex flex-col gap-4">
        <Link to="/donor/children" className="text-sm font-medium text-forest-700 hover:underline">
          ← Back to my children
        </Link>
        <div className="rounded-[10px] border border-gold-500 bg-white p-8 text-center">
          <p className="m-0 text-sm text-ink/60">{error}</p>
        </div>
      </div>
    )
  }

  if (!detail) {
    return <DonorChildDetailSkeleton />
  }

  const { child, academicTerms } = detail

  return (
    <div className="flex flex-col gap-6">
      <Link to="/donor/children" className="text-sm font-medium text-forest-700 hover:underline">
        ← Back to my children
      </Link>

      <div className="border border-gold-500 rounded-[10px] bg-white overflow-hidden">
        <div className="grid grid-cols-1 gap-4 p-5 sm:grid-cols-[auto_1fr] sm:gap-6 sm:p-8">
          {child.photoUrl && (
            <img src={child.photoUrl} alt={child.name} className="w-24 h-24 rounded-[10px] object-cover sm:w-40 sm:h-40" />
          )}
          <div className="flex flex-col">
            <h1 className="m-0 text-[#0b140d] font-display text-2xl font-bold leading-9 tracking-tight sm:text-[28px]">
              {child.name}
            </h1>
            <div className="grid grid-cols-2 gap-x-6 gap-y-3 mt-3 sm:grid-cols-4 sm:gap-y-2">
              <div className="flex flex-col gap-0.5">
                <span className="text-[rgba(9,49,27,0.7)] text-[13px] font-medium">Age</span>
                <span className="text-[#0b140d] text-sm font-semibold">{child.age}</span>
              </div>
              <div className="flex flex-col gap-0.5">
                <span className="text-[rgba(9,49,27,0.7)] text-[13px] font-medium">Class</span>
                <span className="text-[#0b140d] text-sm font-semibold">{child.classLevel}</span>
              </div>
              <div className="col-span-2 flex flex-col gap-0.5">
                <span className="text-[rgba(9,49,27,0.7)] text-[13px] font-medium">School</span>
                <span className="text-[#0b140d] text-sm font-semibold">{child.school}</span>
              </div>
              <div className="col-span-2 flex flex-col gap-0.5 mt-2">
                <span className="text-[rgba(9,49,27,0.7)] text-[13px] font-medium">Sponsoring since</span>
                <span className="text-[#0b140d] text-sm font-semibold">
                  {child.since ? new Date(child.since).toLocaleDateString(undefined, { month: "long", year: "numeric" }) : "Not available"}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-3">
        <div>
          <h2 className="m-0 text-ink font-display text-xl font-bold">Academic results</h2>
          <p className="m-0 mt-1 text-sm text-ink/60">Term by term results and teacher summaries.</p>
        </div>
        {academicTerms.length === 0 ? (
          <div className="rounded-[10px] border border-gold-500 bg-white p-6 text-center">
            <p className="m-0 text-sm text-ink/60">No results published yet.</p>
          </div>
        ) : academicTerms.map((term) => (
          <TermCard
            key={term.id}
            term={term}
            school={child.school}
            classLevel={child.classLevel}
            expanded={term.id === selectedTermId}
            onToggle={() => setSelectedTermId((current) => (current === term.id ? "" : term.id))}
          />
        ))}
      </div>

      <div className="rounded-[10px] border border-gold-500 bg-white">
        <div className="border-b border-gold-500/40 p-5">
          <h2 className="m-0 text-ink font-display text-xl font-bold">Documents</h2>
          <p className="m-0 mt-1 text-sm text-ink/60">Reports and records shared by Redempta.</p>
        </div>
        {child.documents.length === 0 ? (
          <p className="m-0 p-5 text-sm text-ink/60">No documents uploaded yet.</p>
        ) : (
          <ul className="m-0 flex list-none flex-col p-0">
            {child.documents.map((document, index) => (
              <li
                key={`${document.name}:${document.uploadedAt}`}
                className={`flex flex-col gap-2 p-5 sm:flex-row sm:items-center sm:justify-between ${index > 0 ? "border-t border-gold-500/40" : ""}`}
              >
                <div className="min-w-0">
                  <p className="m-0 text-sm font-semibold text-ink">{document.name}</p>
                  <p className="m-0 mt-1 text-xs text-ink/60">
                    {documentLabel(document.purpose)} · {new Date(document.uploadedAt).toLocaleDateString()} · {formatSize(document.sizeBytes)}
                  </p>
                </div>
                <a
                  href={document.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex shrink-0 items-center rounded-full border border-forest-700 px-4 py-2 text-sm font-medium text-forest-700 transition-colors hover:bg-cream-100"
                >
                  View
                </a>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}
